import "reflect-metadata"; 
import Keycloak from 'keycloak-js'
import { Logger } from './Logger'
import KeycloakAuthorization from 'keycloak-js/authz'
import { Expose, plainToClass, Transform, Type } from 'class-transformer'

export enum PermissionScope {
  READ = 'read',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete'
}

export class Authorization {

  @Expose()
  @Type(() => Permission)
  permissions?: Permission[]

  hasPermission(resourceName: string, scope: PermissionScope): boolean {
    const resource = this.permissions?.find(permission => permission.name === resourceName);
    return resource?.hasScope(scope) ?? false;
  }

  hasAnyOfType(resourceName: string, scope: PermissionScope): boolean {
    return this.permissions?.some(permission => permission.name === resourceName && permission.hasScope(scope)) ?? false;
  }
}

class Permission {
  @Expose({ name: 'rsid' })
  id?: string;

  @Expose({ name: 'rsname' })
  name?: string;
  
  @Expose()
  @Transform(({ value }) => value as PermissionScope[])
  scopes!: PermissionScope[];

  hasScope(scope: PermissionScope): boolean {
    return this.scopes?.includes(scope) ?? false;
  }
}

export class ParsedToken {
  @Expose()
  channel_partner_id?: string | number

  @Expose()
  groups?: string[]

  @Expose()
  sub?: string

  @Expose()
  iat?: number

  @Expose()
  exp?: number

  @Expose()
  iss?: string

  @Expose()
  aud?: string | string[]

  @Expose()
  typ?: string

  @Expose()
  azp?: string

  @Expose()
  session_state?: string

  @Expose()
  acr?: string

  @Expose()
  realm_access?: {
    roles?: string[]
  }

  @Expose()
  resource_access?: Record<string, {
    roles?: string[]
  }>

  @Expose()
  scope?: string

  @Expose()
  email_verified?: boolean

  @Expose()
  name?: string

  @Expose({ name: 'preferred_username' })
  username?: string

  @Expose({ name: 'given_name' })
  firstName?: string

  @Expose({ name: 'family_name' })
  lastName?: string

  @Expose()
  email?: string
  
  @Expose()
  @Type(() => Authorization)
  authorization?: Authorization
}

class AuthService {
  public keycloak: Keycloak
  private keycloakAuthorization: KeycloakAuthorization | undefined

  constructor() {
    Logger.info('Initializing AuthService with Keycloak config', {
      url: import.meta.env.VITE_KEYCLOAK_URL,
      realm: import.meta.env.VITE_KEYCLOAK_REALM,
      clientId: import.meta.env.VITE_KEYCLOAK_FRONTEND_CLIENT_ID
    });
    
    this.keycloak = new Keycloak({
      url: import.meta.env.VITE_KEYCLOAK_URL,
      realm: import.meta.env.VITE_KEYCLOAK_REALM,
      clientId: import.meta.env.VITE_KEYCLOAK_FRONTEND_CLIENT_ID
    })
  }

  async init() {
    const authenticated =  await this.keycloak.init({
      onLoad: 'login-required',
      pkceMethod: 'S256',
      checkLoginIframe: false
    })

    if (authenticated) {
      await this.getToken();

      if(!this.keycloakAuthorization?.rpt) {
        await this.loadPermissions();
      }
    }

    return authenticated;
  }

  async loadPermissions(): Promise<void>  {
    this.keycloakAuthorization = new KeycloakAuthorization(this.keycloak);

    const clientId = import.meta.env.VITE_KEYCLOAK_BACKEND_CLIENT_ID;
    try {
        await this.keycloakAuthorization.entitlement(clientId);
    } catch (error) {
        console.error('Authorization request failed', error);
    }
  }
  
  async getToken(): Promise<string | undefined> {
    try {
      return this.keycloakAuthorization?.rpt;
    } catch (error) {
      Logger.error('Failed to retrieve token', error)
      return undefined
    }
  }

  getUsername(): string {
    return this.parsedToken?.name || 'Unknown'
  }

  async refreshToken() {
    try {
      const refreshed = await this.keycloak.updateToken(30);
      if(refreshed || !this.keycloakAuthorization?.rpt) {
        await this.loadPermissions();
      }
    } catch (error) {
      Logger.error('Failed to refresh token', error)
    }
  }

  login() {
    this.keycloak.login()
  }

  logout() {
    if (this.keycloakAuthorization) {
      this.keycloakAuthorization.rpt = null;
      this.keycloakAuthorization = undefined;
    }

    this.keycloak.logout({ redirectUri: window.location.origin })
    localStorage.removeItem("navbar_state")
  }

  isTokenExpired(minValidity: number = 30): boolean {
    if (!this.keycloak.token) return true;
    return this.keycloak.isTokenExpired(minValidity);
  }

  get authenticated(): boolean {
    return this.keycloak.authenticated;
  }

  get parsedToken(): ParsedToken | null {
    const rpt = this.keycloakAuthorization?.rpt;
    if (!rpt) return null;

    try {
      const rawJson = JSON.parse(atob(rpt.split('.')[1]));
      return plainToClass(ParsedToken, rawJson, {
        excludeExtraneousValues: true
      });
    } catch (e) {
      console.error("Failed to parse RPT", e);
      return null;
    }
  }

  hasPermission(
    resource: string | readonly string[], 
    scope: PermissionScope | readonly string[]
  ): boolean {
    const parsedToken = this.parsedToken;
    const authorization = parsedToken?.authorization;
  
    if (!authorization) return false;
  
    const resources = Array.isArray(resource) ? resource : [resource];
    const scopes = Array.isArray(scope) ? scope : [scope];

    return resources.some(res => 
      scopes.some(s => 
        authorization.hasPermission(res, s) ||
        authorization.hasAnyOfType(res, s)
      )
    );
  }

  hasAllPermissions(
    resource: string | readonly string[], 
    scope: PermissionScope
  ): boolean {
    const authorization = this.parsedToken?.authorization;
  
    if (!authorization) return false;
  
    const resources = Array.isArray(resource) ? resource : [resource];

    return resources.every(res => 
      authorization.hasPermission(res, scope)
    );
  }

  get channelPartnerId(): number | null {
    const token = this.parsedToken
    const roles = token?.realm_access?.roles || []

    if (token?.channel_partner_id == null || token.channel_partner_id == 0) {
      return null
    }

    if (token?.groups && Array.isArray(token.groups)) {
      for (const group of token.groups) {
        const match = group.match(/Channel Partner (\d+)/i)
        if (match && match[1]) return parseInt(match[1], 10)
      }
    }

    return token?.channel_partner_id ? Number(token.channel_partner_id) : null
  }
}

export const authService = new AuthService()